import React from 'react';

import { SortOrder } from 'src/types/utility';

import BackendService from 'src/services/BackendService';
import dayjs from 'dayjs';
import {
  CSV_FORMAT_WITHOUT_SECONDS,
  getFormatDateByContinent,
} from 'src/utils/timeUtils';

import { initFileDownloadUsingAnchorElement } from 'src/utils/fileUtils';
import {
  actionSuccessNotification,
  notifyUserByActionTypeAndCode,
} from 'src/utils//errorHandling/notifications';
import { fileActions } from 'src/utils/constants';
import { StyledUtilizationCell } from 'src/routes/GMDashboard/components/SubtenantCards/ListView/styled';
import {
  getCurrentUtilizationBackground,
  getUtilizationGapBackground,
} from 'src/routes/GMDashboard/components/SubtenantCards/ListView/utils';
import DisconnectionOverviewCell from 'src/routes/SitesGroups/components/DisconnectionOverviewCell';

export const getSiteGroupsColumns = (
  intl,
  messages,
  currentCustomer,
  serverSortingColumns,
) => [
  {
    title: intl.formatMessage(messages.name),
    dataIndex: 'groupName',
    key: 'groupName',
    filtered: true,
    onFilter: (value, record) =>
      record.name.toLowerCase().includes(value.toLowerCase()),
    sorter: serverSortingColumns.includes('groupName'),
    defaultSortOrder: 'ascend',
  },
  !currentCustomer
    ? {
        ...{
          title: intl.formatMessage(messages.customerLabel),
          dataIndex: 'customerName',
          key: 'customerName',
        },
      }
    : null,
  {
    title: intl.formatMessage(messages.devicesLabel),
    dataIndex: 'devicesCount',
    key: 'devicesCount',
    width: 100,
    sorter: serverSortingColumns.includes('devicesCount'),
  },
  {
    title: intl.formatMessage(messages.connectedAndAssignedLabel),
    dataIndex: 'connectedAssigned',
    key: 'connectedAssigned',
    width: 100,
    sorter: serverSortingColumns.includes('connectedAssigned'),
  },
  {
    title: intl.formatMessage(messages.connectedAndUnassignedLabel),
    dataIndex: 'connectedUnassigned',
    key: 'connectedUnassigned',
    width: 100,
    sorter: serverSortingColumns.includes('connectedUnassigned'),
  },
  {
    title: intl.formatMessage(messages.disconnectedAndAssignedLabel),
    dataIndex: 'disconnectedAssigned',
    key: 'disconnectedAssigned',
    width: 100,
    sorter: serverSortingColumns.includes('disconnectedAssigned'),
    render: (_, { id, groupName, disconnectedAssigned }) => {
      return (
        <DisconnectionOverviewCell
          groupId={id}
          subtenantName={groupName}
          value={disconnectedAssigned}
        />
      );
    },
  },
  {
    title: intl.formatMessage(messages.disconnectedAndUnassignedLabel),
    dataIndex: 'disconnectedUnassigned',
    key: 'disconnectedUnassigned',
    width: 100,
    sorter: serverSortingColumns.includes('disconnectedUnassigned'),
    render: (_, { id, groupName, disconnectedUnassigned }) => {
      return (
        <DisconnectionOverviewCell
          groupId={id}
          subtenantName={groupName}
          value={disconnectedUnassigned}
        />
      );
    },
  },
  {
    title: intl.formatMessage(messages.noConsentLabel),
    dataIndex: 'noConsent',
    key: 'noConsent',
    width: 100,
    sorter: serverSortingColumns.includes('noConsent'),
  },
  {
    title: intl.formatMessage(messages.currentUtilization),
    dataIndex: 'currentUtilization',
    key: 'currentUtilization',
    sorter: serverSortingColumns.includes('currentUtilization'),
    width: 100,
    render: (_, { currentUtilization }) => (
      <StyledUtilizationCell
        backgroundColor={getCurrentUtilizationBackground(
          Math.round(currentUtilization),
        )}
        className="list-view-percentage-cell"
      >
        {`${Math.round(currentUtilization)}%`}
      </StyledUtilizationCell>
    ),
  },
  {
    title: intl.formatMessage(messages.potentialUtilization),
    dataIndex: 'potentialUtilization',
    key: 'potentialUtilization',
    filtered: true,
    sorter: serverSortingColumns.includes('potentialUtilization'),
    width: 100,
    render: (_, { potentialUtilization }) => (
      <StyledUtilizationCell
        backgroundColor={getCurrentUtilizationBackground(
          Math.round(potentialUtilization),
        )}
        className="list-view-percentage-cell"
      >
        {`${Math.round(potentialUtilization)}%`}
      </StyledUtilizationCell>
    ),
  },
  {
    title: intl.formatMessage(messages.gapUtilization),
    dataIndex: 'utilizationGap',
    key: 'utilizationGap',
    filtered: true,
    width: 100,
    sorter: serverSortingColumns.includes('utilizationGap'),
    render: (_, { utilizationGap }) => (
      <StyledUtilizationCell
        backgroundColor={getUtilizationGapBackground(
          Math.round(utilizationGap),
        )}
        className="list-view-percentage-cell"
      >
        {`${Math.round(utilizationGap)}%`}
      </StyledUtilizationCell>
    ),
  },
];

export const getCurrentOrder = sorter => {
  if (sorter.order === 'descend') {
    return SortOrder.desc;
  }
  if (sorter.order === 'ascend') {
    return SortOrder.asc;
  }
  return '';
};
export const getSiteGroupsTableActions = (
  intl,
  messages,
  excludedFromDeleteTenants,
  setIsConfirmationModalVisible,
  setActiveActionRow,
  setIsEditModalVisible,
  setModalMode,
) => [
  {
    key: 'delete',
    name: intl.formatMessage(messages.delete),
    isDisabled: ({ record }) => excludedFromDeleteTenants.includes(record.id),
    onClick: (_, { record }) => {
      setIsConfirmationModalVisible(true);
      setActiveActionRow(record);
    },
  },
  {
    key: 'edit',
    name: intl.formatMessage(messages.edit),
    onClick: (_, { record }) => {
      setActiveActionRow(record);
      setIsEditModalVisible(true);
      setModalMode(tenantModalModes.EDIT);
    },
  },
];
export const tenantModalModes = {
  ADD: 'ADD',
  EDIT: 'EDIT',
};

export const exportCsvData = (tableParams, setIsCsvLoading) => {
  if (!tableParams) {
    return;
  }
  setIsCsvLoading(true);
  const { searchKeyword = '', sortBy, filters } = tableParams;

  const downloadedFileName = `TotalGroups_${dayjs().format(
    getFormatDateByContinent(CSV_FORMAT_WITHOUT_SECONDS),
  )}.csv`;
  BackendService.exportCsvAdminSiteGroups({
    paginationParams: { page: 0, limit: 100000 },
    searchKeyword,
    filters,
    sortBy,
  })
    .then(response => {
      const type = response.headers['content-type'];
      const blob = new Blob([response.data], { type: type, encoding: 'UTF-8' });

      initFileDownloadUsingAnchorElement(
        URL.createObjectURL(blob),
        downloadedFileName,
      );

      setIsCsvLoading(false);
      actionSuccessNotification(fileActions.DOWNLOAD, downloadedFileName);
    })
    .catch(err => {
      notifyUserByActionTypeAndCode(
        fileActions.DOWNLOAD,
        downloadedFileName,
        err,
      );
      setIsCsvLoading(false);
      console.log(err);
    });
};
